import { useState, useEffect, useCallback } from "react";
import { Button } from "@/components/ui/button";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogDescription,
  DialogFooter,
} from "@/components/ui/dialog";
import { Switch } from "@/components/ui/switch";
import { Label } from "@/components/ui/label";
import { Shield } from "lucide-react";

const CONSENT_KEY = "nutriflow_cookie_consent";

interface CookiePreferences {
  necessary: true; // always on
  analytics: boolean;
  marketing: boolean;
  timestamp: string;
}

function loadConsent(): CookiePreferences | null {
  try {
    const stored = localStorage.getItem(CONSENT_KEY);
    if (!stored) return null;
    return JSON.parse(stored) as CookiePreferences;
  } catch {
    return null;
  }
}

function saveConsent(prefs: CookiePreferences) {
  localStorage.setItem(CONSENT_KEY, JSON.stringify(prefs));
}

export function CookieConsent() {
  const [visible, setVisible] = useState(false);
  // F-02-08: analytics/marketing switches removed — no trackers are integrated.
  // Only technically-necessary cookies are used (auth sessions, security).
  const [showSettings, setShowSettings] = useState(false);

  useEffect(() => {
    const existing = loadConsent();
    if (!existing) {
      setVisible(true);
    }
  }, []);

  const accept = useCallback((prefs: CookiePreferences) => {
    saveConsent(prefs);
    setVisible(false);
    setShowSettings(false);
  }, []);

  const handleAcceptAll = useCallback(() => {
    accept({
      necessary: true,
      analytics: false,
      marketing: false,
      timestamp: new Date().toISOString(),
    });
  }, [accept]);

  const handleNecessaryOnly = handleAcceptAll;
  const handleSaveSettings = handleAcceptAll;

  if (!visible) return null;

  return (
    <>
      {/* Banner */}
      {!showSettings && (
        <div
          role="dialog"
          aria-label="Cookie-Einstellungen"
          className="fixed bottom-0 left-0 right-0 z-50 border-t bg-background p-4 shadow-lg sm:p-6"
        >
          <div className="mx-auto flex max-w-5xl flex-col gap-4 sm:flex-row sm:items-center sm:justify-between">
            <div className="flex items-start gap-3">
              <Shield className="mt-0.5 h-5 w-5 shrink-0 text-primary" />
              <div className="space-y-1">
                <p className="text-sm font-medium">
                  Diese Website verwendet Cookies
                </p>
                <p className="text-xs text-muted-foreground">
                  Wir nutzen Cookies, um Ihnen die bestmögliche Erfahrung zu
                  bieten. Notwendige Cookies sind für den Betrieb der Website
                  erforderlich. Sie können Ihre Einstellungen jederzeit anpassen.
                </p>
              </div>
            </div>
            <div className="flex shrink-0 flex-col gap-2 sm:flex-row">
              <Button variant="outline" size="sm" onClick={() => setShowSettings(true)}>
                Einstellungen
              </Button>
              <Button variant="outline" size="sm" onClick={handleNecessaryOnly}>
                Nur notwendige
              </Button>
              <Button size="sm" onClick={handleAcceptAll}>
                Alle akzeptieren
              </Button>
            </div>
          </div>
        </div>
      )}

      {/* Settings Dialog */}
      <Dialog open={showSettings} onOpenChange={setShowSettings}>
        <DialogContent className="sm:max-w-md">
          <DialogHeader>
            <DialogTitle>Cookie-Einstellungen</DialogTitle>
            <DialogDescription>
              Wählen Sie, welche Cookies Sie zulassen möchten. Notwendige Cookies
              können nicht deaktiviert werden, da sie für den sicheren Betrieb
              der Anwendung erforderlich sind.
            </DialogDescription>
          </DialogHeader>

          <div className="space-y-4 py-2">
            {/* Necessary - always on */}
            <div className="flex items-center justify-between rounded-md border p-3">
              <div className="space-y-0.5">
                <Label className="text-sm font-medium">Notwendig</Label>
                <p className="text-xs text-muted-foreground">
                  Authentifizierung, Sicherheit, grundlegende Funktionen
                </p>
              </div>
              <Switch checked disabled aria-label="Notwendige Cookies (immer aktiv)" />
            </div>

          </div>

          <DialogFooter className="gap-2 sm:gap-0">
            <Button variant="outline" onClick={handleNecessaryOnly}>
              Nur notwendige
            </Button>
            <Button onClick={handleSaveSettings}>Auswahl speichern</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </>
  );
}
